/**
 * Hooks for the Founder Dashboard tiles — thin react-query wrappers
 * over the gateway-proxied /api/dashboard/* endpoints. Stale times
 * are aligned with the Redis TTL on the service side.
 */
import { useQuery } from '@tanstack/react-query';

import { apiJson } from '../../lib/api/client';
import type {
  CrossProjectKPIsResponse,
  DepartmentHealthGrid,
  IncidentsResponse,
  NeedsAttentionResponse,
  RecentDecisionsResponse,
} from './dashboard-types';

export const dashboardKeys = {
  all: ['dashboard'] as const,
  departmentHealth: () => [...dashboardKeys.all, 'departments', 'health'] as const,
  incidents: () => [...dashboardKeys.all, 'incidents'] as const,
  recentDecisions: (limit: number) => [...dashboardKeys.all, 'decisions', 'recent', limit] as const,
  crossProjectKpis: (window: string) =>
    [...dashboardKeys.all, 'kpis', 'cross-project', window] as const,
  needsAttention: () => [...dashboardKeys.all, 'needs-attention'] as const,
};

export function useDepartmentHealth() {
  return useQuery<DepartmentHealthGrid>({
    queryKey: dashboardKeys.departmentHealth(),
    queryFn: () => apiJson<DepartmentHealthGrid>('/dashboard/departments/health'),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}

export function useSystemIncidents() {
  return useQuery<IncidentsResponse>({
    queryKey: dashboardKeys.incidents(),
    queryFn: () => apiJson<IncidentsResponse>('/dashboard/incidents'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useRecentDecisions(limit = 20) {
  return useQuery<RecentDecisionsResponse>({
    queryKey: dashboardKeys.recentDecisions(limit),
    queryFn: () => apiJson<RecentDecisionsResponse>(`/dashboard/decisions/recent?limit=${limit}`),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useCrossProjectKpis(window: '7d' | '30d' | '90d' = '30d') {
  return useQuery<CrossProjectKPIsResponse>({
    queryKey: dashboardKeys.crossProjectKpis(window),
    queryFn: () =>
      apiJson<CrossProjectKPIsResponse>(`/dashboard/kpis/cross-project?window=${window}`),
    staleTime: 120_000,
    refetchInterval: 300_000,
  });
}

export function useNeedsAttention() {
  return useQuery<NeedsAttentionResponse>({
    queryKey: dashboardKeys.needsAttention(),
    queryFn: () => apiJson<NeedsAttentionResponse>('/dashboard/needs-attention'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
